import PropTypes from 'prop-types';
import React, { Component } from 'react';
import {
  ActivityIndicator,
  Dimensions,
  Image,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import Colors from '../../constants/Colors';
import Layout from '../../constants/Layout';
import Plentiful from '../../constants/Plentiful';
import * as featureFlags from '../../featureFlags';
import { logError } from '../../utils/logger';
import { capitalize } from '../../utils/stringFunctions';
import timeUtils from '../../utils/timeUtils';
import ExternalLink from '../ExternalLink';
import FilledButton from '../FilledButton';
import MenuItem from '../MenuItem';
import NavBar from '../NavBar';
import BirthdaySelectModal from '../form/BirthdaySelectModal';
import Logout from './Logout';
import QrCode from './QrCode';

class ProfileLanding extends Component {
  static propTypes = {
    navigation: PropTypes.objectOf(PropTypes.any),
    translation: PropTypes.objectOf(PropTypes.any).isRequired,
    actions: PropTypes.objectOf(PropTypes.any).isRequired,
    profile: PropTypes.objectOf(PropTypes.any).isRequired,
    registration: PropTypes.objectOf(PropTypes.any).isRequired,
    pantries: PropTypes.array,
  };

  constructor(props) {
    super();
    this.state = {
      errors: {},
      user: {},
      showSpinner: false,
      showChangeLanguage: false,
      showSaveChanges: false,
      pushNotificationRegistration: false,
      showAddBirthday: false,
      triggerTestError: false,
    };
  }

  year2000 = 946750000;

  logOut = () => {
    const { actions, navigation } = this.props;
    actions.logout(() => {
      if (Platform.OS === 'web') {
        window.location.href = '/language';
      } else {
        navigation.navigate('Language');
      }
    });
  };

  goToUserInfo = () => {
    const { navigation, actions } = this.props;
    this.setState({ showSpinner: true });
    actions.getCurrentUser(() => {
      this.setState({ showSpinner: false });
      navigation.navigate('info');
    });
  };

  goToUserSettings = () => {
    const { navigation, actions } = this.props;
    this.setState({ showSpinner: true });
    actions.getCurrentUser(() => {
      this.setState({ showSpinner: false });
      navigation.navigate('settings');
    });
  };

  goToUserBenefits = () => {
    const { navigation, actions } = this.props;
    this.setState({ showSpinner: true });
    actions.getCurrentUser(() => {
      this.setState({ showSpinner: false });
      navigation.navigate('benefits');
    });
  };

  addBirthday = () => {
    this.setState({ showBirthdateModal: true });
  };

  cancelBirthdate = () => {
    this.setState({ showBirthdateModal: false });
  };

  updateBirthdate = (value) => {
    const { translation, actions } = this.props;
    const invalid = translation.app_invalid_error;
    const { errors } = this.state;
    let valid = true;
    if (!value) {
      valid = false;
    } else if (!Number.isInteger(value) || value == this.year2000) {
      valid = false;
    }
    if (!valid) {
      errors.birth_date = `${invalid} ${translation.app_personal_edittext_dob}`;
    } else {
      errors.birth_date = null;
      actions.updateUser({ birth_date: value });
    }
    this.setState({ errors, showBirthdateModal: false });
  };

  render() {
    const { translation, profile, pantries, locale, registration, actions } = this.props;
    const { showBirthdateModal, showSpinner, triggerTestError } = this.state;

    // Test ErrorBoundary functionality - throw during render when flag is set
    if (triggerTestError) {
      throw new Error('Test error for ErrorBoundary - triggered from ProfileLanding render');
    }
    const labels = {
      profile: capitalize(translation.fragment_main_bottombar_profile),
      notifications: translation.fragment_profile_textview_notifications,
      personal_details: capitalize(translation.fragment_profile_textview_personaldetails),
      personal_details_extra: translation.personal_details_extra,
      settings: capitalize(translation.settings),
      settings_extra: translation.settings_extra,
      benefits: capitalize(translation.benefits),
      benefits_extra: translation.benefits_extra,
    };

    let birthdateModal = null;
    if (showBirthdateModal) {
      birthdateModal = (
        <BirthdaySelectModal
          translation={translation}
          locale={locale}
          onCancel={this.cancelBirthdate}
          onSelect={this.updateBirthdate}
          date={profile.birth_date || this.year2000}
        />
      );
    }

    const { SUPPORT_EMAIL } = Plentiful;

    /**
     * Need to find user id from simple number and use that number
     * also to generate QR code
     * Add 1 billion to the number, then go from dec to octal number, and show in reverse
     * 1000000001 -> 7346545001 = 1005 4564 37
     * 1999999999 -> 16715311777 = 7771 1351 761
     */
    const code = 1000000000 + parseInt(profile.id, 10);
    const oct = code.toString(8);
    let qrCodeValue = '';
    for (let i = 0; i < oct.length; i++) {
      qrCodeValue += oct[oct.length - 1 - i];
      if (i % 4 === 3) qrCodeValue += ' ';
    }
    qrCodeValue = qrCodeValue.trim();
    const position = 0;
    qrCodeValue = [qrCodeValue.slice(0, position), 'P-', qrCodeValue.slice(position)].join('');

    // verified: 3 options
    // 1. Not verified yet
    // 2. Verified, but verification has expired
    // 3. Verified, not expired
    let verifiedStyle = 'notVerifiedText';
    let verifyIcon = 'unverified';
    let verifyColor = Colors.brightRed;
    const now = new Date();
    if (!profile.last_verified) {
      labels.verified = translation.not_verified;
    } else if (profile.reverify_before) {
      let pantryName = '';
      pantries.forEach((rec) => {
        if (rec.id == profile.verified_at) {
          pantryName = rec.name;
        }
      });
      const reverifyBy = timeUtils.formatBirthdate(new Date(profile.reverify_before));
      const verifiedOn = timeUtils.formatBirthdate(profile.last_verified);
      labels.verified = `${translation.must_be_reverified_by} ${reverifyBy}`;
      if (new Date(profile.reverify_before) > now) {
        verifiedStyle = 'verifiedText';
        verifyIcon = 'verified';
        verifyColor = Colors.primaryColor;
        labels.verified = `${translation.last_verified_on} ${verifiedOn} ${translation.at} ${pantryName}`;
      }
    }

    return (
      <View style={styles.container}>
        <NavBar title={labels.profile} />

        {showSpinner ? (
          <View style={styles.spinnerModal}>
            <ActivityIndicator size="large" color={Colors.mediumBlue} />
          </View>
        ) : null}

        <ScrollView style={styles.scrollView} contentContainerStyle={styles.view}>
          <View style={styles.infoCard}>
            {profile.birth_date !== null && profile.birth_date !== '' ? (
              <QrCode translation={translation} code={qrCodeValue} />
            ) : (
              <View style={styles.lockedContainer}>
                <Image
                  style={styles.lockedQrCode}
                  source={require('../../assets/images/locked_plenti_pass.png')}
                />
                <TouchableOpacity onPress={this.addBirthday}>
                  <Text style={styles.textLink}>{translation.add_birthday}</Text>
                </TouchableOpacity>
                <Text style={styles.text}>{translation.birthday_required}</Text>
              </View>
            )}
            {profile.birth_date !== null && profile.birth_date !== '' ? (
              <View style={styles.idContainer}>
                <Text style={styles.headerTitle}>{qrCodeValue}</Text>
              </View>
            ) : null}
          </View>

          <View style={styles.bottomContainer}>
            <View style={styles.bottomContainerContent}>
              <View style={styles.infoCard}>
                <View style={styles.horizontalContainer}>
                  <View style={styles.verifiedTextContainer}>
                    <Text style={styles.largeText}>
                      {profile.first_name} {profile.last_name}
                    </Text>
                  </View>
                </View>
              </View>
              <TouchableOpacity onPress={this.goToUserInfo}>
                <MenuItem
                  name={labels.personal_details}
                  description={labels.personal_details_extra}
                  icon="chevron-forward-outline"
                />
              </TouchableOpacity>
              <TouchableOpacity onPress={this.goToUserBenefits}>
                <MenuItem
                  name={labels.benefits}
                  description={labels.benefits_extra}
                  icon="chevron-forward-outline"
                />
              </TouchableOpacity>
              <TouchableOpacity onPress={this.goToUserSettings}>
                <MenuItem
                  name={labels.settings}
                  description={labels.settings_extra}
                  icon="chevron-forward-outline"
                />
              </TouchableOpacity>
              <TouchableOpacity onPress={() => this.props.navigation.navigate('support')}>
                <MenuItem
                  name={translation.help_and_support}
                  description={translation.help_and_support_extra}
                  icon="chevron-forward-outline"
                />
              </TouchableOpacity>
              <ExternalLink to="https://plentifulapp.com/eula" hrefAttrs={{ target: 'blank' }}>
                <MenuItem
                  name={translation.terms_conditions}
                  description="plentifulapp.com/eula"
                  icon="open-outline"
                />
              </ExternalLink>
              <ExternalLink to="https://plentifulapp.com/privacy" hrefAttrs={{ target: 'blank' }}>
                <MenuItem
                  name={translation.privacy_policy}
                  description="plentifulapp.com/privacy"
                  icon="open-outline"
                />
              </ExternalLink>
              <View style={styles.logoutContainer}>
                <View style={styles.logoutContentContainer}>
                  <Logout translation={translation} logout={this.logOut} />
                </View>
              </View>

              {featureFlags.isInternalProfile(profile) ? (
                <FilledButton
                  onPress={() => {
                    logError('test', { at: new Date().toISOString(), by: profile.email });
                  }}
                  label={'[test] error > Sentry'}
                  full={false}
                  color={Colors.primaryColor}
                  textColor={Colors.primaryColor}
                  backgroundColor={Colors.white}
                  disabled={false}
                  height={40}
                  radius={4}
                />
              ) : null}
              {featureFlags.isInternalProfile(profile) ? (
                <FilledButton
                  onPress={() => {
                    this.setState({ triggerTestError: true });
                  }}
                  label={'[test] render error > ErrorScreen'}
                  full={false}
                  color={Colors.primaryColor}
                  textColor={Colors.white}
                  backgroundColor={Colors.white}
                  disabled={false}
                  height={40}
                  radius={4}
                />
              ) : null}
            </View>
          </View>
        </ScrollView>

        {birthdateModal}
      </View>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
  },
  view: {
    color: Colors.primaryColor,
    backgroundColor: Colors.lightBackground,
    position: 'relative',
  },
  spinnerModal: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    justifyContent: 'center',
    zIndex: 1,
  },
  scrollView: {
    flex: 1,
    maxWidth: 720,
    width: '100%',
  },
  infoCard: {
    padding: 16,
    borderBottomColor: Colors.grayBorder,
    borderBottomWidth: 1,
    backgroundColor: Colors.white,
  },
  tefapContainer: {
    marginTop: 30,
    marginBottom: 24,
  },
  lockedContainer: {
    alignItems: 'center',
  },
  lockedQrCode: {
    width: 206,
    height: 206,
  },
  textLink: {
    fontSize: Layout.isSmall ? 14 : 16,
    color: Colors.primaryColor,
    textTransform: 'uppercase',
    textDecorationLine: 'underline',
    fontWeight: '600',
    width: 240,
    textAlign: 'center',
    marginBottom: 16,
  },
  text: {
    fontSize: Layout.isSmall ? 12 : 16,
    color: Colors.veryDarkGray,
  },
  idContainer: {
    marginVertical: -2,
    alignItems: 'center',
  },
  bottomContainer: {
    backgroundColor: Colors.white,
    marginTop: 10,
    flex: 1,
    alignItems: 'center',
  },
  bottomContainerContent: {
    flex: 1,
    width: '100%',
  },
  headerTitle: {
    fontSize: 20,
    color: Colors.mediumBlue,
    fontWeight: '500',
  },
  largeText: {
    fontSize: 20,
    lineHeight: 29,
    marginTop: 5,
  },
  horizontalContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  verifiedTextContainer: {
    width: Dimensions.get('window').width - 100,
    marginBottom: 5,
  },
  verifiedText: {
    fontSize: 14,
    lineHeight: 18,
  },
  notVerifiedText: {
    fontSize: 14,
    lineHeight: 16,
  },
  hr: {
    borderBottomColor: Colors.grayBorder,
    borderBottomWidth: 1,
    width: '100%',
    marginVertical: 12,
  },
  textContainer: {
    flexDirection: 'row',
  },
  logoutContainer: {
    flex: 1,
    alignItems: 'center',
  },
  logoutContentContainer: {
    flex: 1,
    paddingTop: 40,
    paddingBottom: 60,
    width: 256,
    margin: 'auto',
    backgroundColor: Colors.white,
    alignItems: 'stretch',
    justifyContent: 'center',
  },
});

export default ProfileLanding;
